import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../services/firebase";
import { logFirebaseAccessError } from "./firebasePermissionDebug";

const conversationCollection = (companyId) =>
  collection(db, "companies", companyId, "conversations");

export const buildDirectConversationId = (userA, userB) =>
  `direct-${[userA, userB].sort().join("-")}`;

export const getConversationUnreadRecipients = (participantIds, senderId) =>
  [...new Set(participantIds || [])].filter((id) => id && id !== senderId);

export const resolveMentionedMemberIds = (body, members = []) => {
  const text = String(body || "").toLowerCase();
  return members
    .filter((member) => {
      const candidates = [
        member.name,
        member.displayName,
        member.username,
        String(member.email || "").split("@")[0],
      ]
        .filter(Boolean)
        .map((value) => `@${String(value).toLowerCase().replace(/\s+/g, "")}`);
      const compactText = text.replace(/\s+/g, "");
      return candidates.some((candidate) => compactText.includes(candidate));
    })
    .map((member) => member.id || member.uid)
    .filter(Boolean);
};

export const canUserOpenConversation = ({
  companyId,
  userId,
  userCompanyId,
  isOwner,
  participantIds,
}) =>
  Boolean(
    companyId &&
    userId &&
    userCompanyId === companyId &&
    (isOwner || (participantIds || []).includes(userId))
  );

export const loadCompanyMembers = async (companyId) => {
  let snapshot;
  try {
    snapshot = await getDocs(collection(db, "companies", companyId, "members"));
  } catch (error) {
    logFirebaseAccessError({
      error,
      path: `companies/${companyId}/members`,
      operation: "read",
      companyId,
      context: "loadCompanyMembers",
    });
    throw error;
  }
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
};

export const subscribeToConversations = ({ companyId, userId, isOwner, onData, onError }) => {
  const source = isOwner
    ? conversationCollection(companyId)
    : query(
        conversationCollection(companyId),
        where("participantIds", "array-contains", userId)
      );
  return onSnapshot(source, (snapshot) => {
    onData(snapshot.docs
      .map((item) => ({ id: item.id, ...item.data() }))
      .sort((a, b) => {
        const aTime = typeof a.updatedAt?.toMillis === "function" ? a.updatedAt.toMillis() : 0;
        const bTime = typeof b.updatedAt?.toMillis === "function" ? b.updatedAt.toMillis() : 0;
        return bTime - aTime;
      }));
  }, (error) => {
    logFirebaseAccessError({
      error,
      path: isOwner
        ? `companies/${companyId}/conversations`
        : `companies/${companyId}/conversations?participantIds=${userId}`,
      operation: "listen",
      user: { uid: userId },
      role: isOwner ? "owner" : "",
      companyId,
      context: "subscribeToConversations",
    });
    onError?.(error);
  });
};

export const createOrOpenDirectConversation = async ({
  companyId,
  currentUser,
  recipient,
}) => {
  const conversationId = buildDirectConversationId(currentUser.uid, recipient.id);
  const reference = doc(db, "companies", companyId, "conversations", conversationId);
  const existing = await getDoc(reference);
  if (!existing.exists()) {
    await setDoc(reference, {
      companyId,
      type: "direct",
      title: recipient.name || recipient.username || recipient.email || "Direct Message",
      participantIds: [currentUser.uid, recipient.id],
      participantProfiles: {
        [currentUser.uid]: {
          name: currentUser.displayName || currentUser.email || "User",
          email: currentUser.email || "",
        },
        [recipient.id]: {
          name: recipient.name || recipient.username || recipient.displayName || recipient.email || "Team Member",
          email: recipient.email || "",
        },
      },
      unreadBy: [],
      createdBy: currentUser.uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastMessage: "",
    });
  }
  return conversationId;
};

export const createContextConversation = async ({
  companyId,
  currentUser,
  participantIds,
  participantProfiles,
  contextType,
  contextId,
  contextLabel,
}) => {
  const reference = await addDoc(conversationCollection(companyId), {
    companyId,
    type: "context",
    title: contextLabel || `${contextType} discussion`,
    contextType,
    contextId,
    contextLabel,
    participantIds: [...new Set([currentUser.uid, ...participantIds])],
    participantProfiles,
    unreadBy: [],
    createdBy: currentUser.uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastMessage: "",
  });
  return reference.id;
};

export const createGroupConversation = async ({
  companyId,
  currentUser,
  title,
  participantIds,
  participantProfiles,
}) => {
  const cleanTitle = String(title || "").trim();
  if (!cleanTitle) throw new Error("Group name is required.");
  const participants = [...new Set([currentUser.uid, ...participantIds])];
  if (participants.length < 3) {
    throw new Error("A group conversation needs at least three participants.");
  }
  const reference = await addDoc(conversationCollection(companyId), {
    companyId,
    type: "group",
    title: cleanTitle,
    participantIds: participants,
    participantProfiles,
    unreadBy: [],
    createdBy: currentUser.uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastMessage: "",
  });
  return reference.id;
};

export const updateGroupConversation = async ({
  companyId,
  conversationId,
  title,
  participantIds,
  participantProfiles,
}) => {
  const cleanTitle = String(title || "").trim();
  if (!cleanTitle) throw new Error("Group name is required.");
  if ((participantIds || []).length < 3) {
    throw new Error("A group conversation needs at least three participants.");
  }
  return updateDoc(doc(db, "companies", companyId, "conversations", conversationId), {
    title: cleanTitle,
    participantIds: [...new Set(participantIds)],
    participantProfiles,
    updatedAt: serverTimestamp(),
  });
};

export const subscribeToMessages = ({ companyId, conversationId, onData, onError }) =>
  onSnapshot(
    query(
      collection(db, "companies", companyId, "conversations", conversationId, "messages"),
      orderBy("createdAt", "asc")
    ),
    (snapshot) => onData(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))),
    onError
  );

export const sendConversationMessage = async ({
  companyId,
  conversation,
  user,
  body,
  attachments = [],
  mentionedUserIds = [],
}) => {
  const text = body.trim();
  if (!text && !attachments.length) return;
  const messageReference = doc(
    collection(db, "companies", companyId, "conversations", conversation.id, "messages")
  );
  const batch = writeBatch(db);
  batch.set(messageReference, {
      companyId,
      conversationId: conversation.id,
      senderId: user.uid,
      senderName: user.displayName || user.email || "User",
      body: text,
      createdAt: serverTimestamp(),
      attachmentMetadata: attachments,
      mentionedUserIds: [...new Set(mentionedUserIds)].filter((id) =>
        (conversation.participantIds || []).includes(id)
      ),
  });
  const unreadRecipients = getConversationUnreadRecipients(
    conversation.participantIds,
    user.uid
  );
  batch.update(doc(db, "companies", companyId, "conversations", conversation.id), {
    lastMessage: (text || `${attachments.length} attachment${attachments.length === 1 ? "" : "s"}`).slice(0, 240),
    lastSenderId: user.uid,
    unreadBy: unreadRecipients,
    updatedAt: serverTimestamp(),
  });
  unreadRecipients.forEach((recipientId) => {
    batch.set(doc(collection(db, "companies", companyId, "notifications")), {
      companyId,
      recipientId,
      senderId: user.uid,
      senderName: user.displayName || user.email || "User",
      conversationId: conversation.id,
      messageId: messageReference.id,
      conversationTitle: conversation.title || conversation.contextLabel || "Conversation",
      preview: text.slice(0, 160) || "Sent an attachment",
      type: mentionedUserIds.includes(recipientId) ? "mention" : "message",
      read: false,
      createdAt: serverTimestamp(),
    });
  });
  try {
    await batch.commit();
  } catch (error) {
    logFirebaseAccessError({
      error,
      path: `companies/${companyId}/conversations/${conversation.id}/messages`,
      operation: "create",
      user,
      companyId,
      context: "sendConversationMessage",
    });
    throw error;
  }
};

const EVENT_NOTIFICATION_LABELS = {
  fileUploaded: "File uploaded",
  planStatusChanged: "Plan status changed",
  aiDraftSaved: "AI draft saved",
  inspectionCompleted: "Inspection completed",
  userInvited: "User invited",
  toolAttention: "Tool requires attention",
  machineAttention: "Machine status changed",
  approvalRequested: "Approval requested",
  approvalDecision: "Approval decision",
};

export const createCompanyEventNotification = async ({
  companyId,
  recipientIds = [],
  actor,
  type,
  title,
  preview = "",
  contextRoute = "",
  contextType = "",
  contextId = "",
  metadata = {},
}) => {
  const cleanRecipients = [...new Set(recipientIds)].filter(
    (recipientId) => recipientId && recipientId !== actor?.uid
  );
  if (!companyId || cleanRecipients.length === 0) return;

  const batch = writeBatch(db);
  cleanRecipients.slice(0, 100).forEach((recipientId) => {
    batch.set(doc(collection(db, "companies", companyId, "notifications")), {
      companyId,
      recipientId,
      senderId: actor?.uid || "",
      senderName: actor?.displayName || actor?.email || "ManuCalc",
      type: type || "workspaceEvent",
      title: title || EVENT_NOTIFICATION_LABELS[type] || "Workspace update",
      preview: String(preview || "").slice(0, 220),
      contextRoute,
      contextType,
      contextId,
      metadata,
      read: false,
      createdAt: serverTimestamp(),
    });
  });
  try {
    await batch.commit();
  } catch (error) {
    logFirebaseAccessError({
      error,
      path: `companies/${companyId}/notifications`,
      operation: "create",
      user: actor,
      companyId,
      context: "createCompanyEventNotification",
    });
    throw error;
  }
};

export const notifyCompanyOwners = async ({
  companyId,
  actor,
  members = [],
  ...notification
}) => {
  const ownerIds = members
    .filter((member) => ["owner", "admin"].includes(String(member.role || "").toLowerCase()))
    .map((member) => member.id || member.uid)
    .filter(Boolean);
  return createCompanyEventNotification({
    companyId,
    actor,
    recipientIds: ownerIds,
    ...notification,
  });
};

export const markConversationRead = async (companyId, conversation, userId) => {
  if (!(conversation.unreadBy || []).includes(userId)) return;
  await updateDoc(doc(db, "companies", companyId, "conversations", conversation.id), {
    unreadBy: (conversation.unreadBy || []).filter((id) => id !== userId),
  });
};

export const subscribeToCompanyNotifications = ({
  companyId,
  userId,
  onData,
  onError,
}) =>
  onSnapshot(
    query(
      collection(db, "companies", companyId, "notifications"),
      where("recipientId", "==", userId),
      orderBy("createdAt", "desc"),
      limit(200)
    ),
    (snapshot) =>
      onData(
        snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => notificationCreatedAtMillis(b) - notificationCreatedAtMillis(a))
      ),
    onError
  );

export const markNotificationRead = (companyId, notificationId) =>
  updateDoc(doc(db, "companies", companyId, "notifications", notificationId), {
    read: true,
    readAt: serverTimestamp(),
  });

export const markAllNotificationsRead = async (companyId, notifications = []) => {
  const unread = notifications.filter((notification) => notification.id && !notification.read);
  if (!companyId || unread.length === 0) return;
  for (let index = 0; index < unread.length; index += 400) {
    const batch = writeBatch(db);
    unread.slice(index, index + 400).forEach((notification) => {
      batch.update(doc(db, "companies", companyId, "notifications", notification.id), {
        read: true,
        readAt: serverTimestamp(),
      });
    });
    await batch.commit();
  }
};

export const deleteCompanyNotifications = async (companyId, notifications = []) => {
  const removable = notifications.filter((notification) => notification.id);
  if (!companyId || removable.length === 0) return;
  for (let index = 0; index < removable.length; index += 400) {
    const batch = writeBatch(db);
    removable.slice(index, index + 400).forEach((notification) => {
      batch.delete(doc(db, "companies", companyId, "notifications", notification.id));
    });
    await batch.commit();
  }
};

export const notificationCreatedAtMillis = (notification = {}) => {
  const firestoreMillis = notification.createdAt?.toMillis?.();
  if (Number.isFinite(firestoreMillis)) return firestoreMillis;
  const parsed = new Date(notification.createdAt || 0).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

export const getNotificationCategory = (notification = {}) =>
  ["message", "mention"].includes(notification.type) || notification.conversationId
    ? "messages"
    : "workspace";

export const filterCompanyNotifications = (notifications = [], { filter = "all", queryText = "" } = {}) => {
  const needle = String(queryText).trim().toLowerCase();
  return notifications.filter((notification) => {
    const category = getNotificationCategory(notification);
    const matchesFilter =
      filter === "all" ||
      (filter === "unread" && !notification.read) ||
      filter === category;
    const searchable = [
      notification.title,
      notification.preview,
      notification.senderName,
      notification.conversationTitle,
      notification.contextLabel,
      notification.contextType,
    ].filter(Boolean).join(" ").toLowerCase();
    return matchesFilter && (!needle || searchable.includes(needle));
  });
};
